import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import debounce from 'lodash/debounce';
import { NumberRenderer } from '@client-shared/ui-numeral';

export default class AgGridWithCount extends PureComponent {
  static propTypes = {
    onBodyScroll: PropTypes.func,
    onFilterChanged: PropTypes.func,
    onGridReady: PropTypes.func,
    onGridSizeChanged: PropTypes.func,
    onRowDataUpdated: PropTypes.func,
    onFirstDataRendered: PropTypes.func,
    rowData: PropTypes.arrayOf(PropTypes.object), // eslint-disable-line react/forbid-prop-types
  };

  static defaultProps = {
    onBodyScroll: null,
    onFilterChanged: null,
    onGridReady: null,
    onGridSizeChanged: null,
    onRowDataUpdated: null,
    onFirstDataRendered: null,
  };

  state = {
    first: 0,
    last: 0,
    count: 0,
  };

  componentDidUpdate(prevProps) {
    if (prevProps.rowData !== this.props.rowData) {
      this.setState(prevState => {
        const newCount = this.props.rowData?.length || 0;
        const newFirstRow = newCount ? 1 : 0;

        let newLastRow = prevState.last;
        if (newCount < prevState.last || newCount === 0) {
          newLastRow = newCount;
        } else if (newCount > prevState.last && prevState.last !== 0) {
          newLastRow = prevState.last;
        }
        if (prevProps.rowData?.length === 0 && newCount > 0) {
          return {
            first: newFirstRow,
            last: newCount,
            count: newCount,
          };
        }

        return {
          first: newFirstRow,
          last: newLastRow,
          count: newCount,
        };
      });
    }
  }

  updateCount = debounce(params => {
    const { top, bottom } = params.api.getVerticalPixelRange();

    const firstRowId = params.api.getFirstDisplayedRowIndex();
    const lastRowId = params.api.getLastDisplayedRowIndex();

    const displayedRows = Array.from(Array(lastRowId - firstRowId + 1).keys());

    const firstDisplayedRow = displayedRows.find(id => {
      const index = id + firstRowId;
      const row = params.api.getDisplayedRowAtIndex(index);

      return (row.rowTop + row.rowHeight) >= top;
    });
    const lastDisplayedRow = displayedRows.sort((idA, idB) => idB - idA).find(id => {
      const index = id + firstRowId;
      const row = params.api.getDisplayedRowAtIndex(index);

      return row.rowTop < bottom;
    });

    this.setState(() => ({
      first: firstDisplayedRow + firstRowId + 1 || 0,
      last: lastDisplayedRow + firstRowId + 1 || 0,
      count: params.api.getDisplayedRowCount(),
    }));
  }, 100);

  onBodyScroll = params => {
    const { onBodyScroll } = this.props;

    if (onBodyScroll) {
      onBodyScroll(params);
    }

    this.updateCount(params);
  };

  onFilterChanged = params => {
    const { onFilterChanged } = this.props;

    if (onFilterChanged) {
      onFilterChanged(params);
    }

    this.updateCount(params);
  };

  onGridReady = params => {
    const { onGridReady } = this.props;

    if (onGridReady) {
      onGridReady(params);
    }

    this.updateCount(params);
  };

  onGridSizeChanged = params => {
    const { onGridSizeChanged } = this.props;

    if (onGridSizeChanged) {
      onGridSizeChanged(params);
    }

    this.updateCount(params);
  };

  onRowDataUpdated = params => {
    const { onRowDataUpdated } = this.props;

    if (onRowDataUpdated) {
      onRowDataUpdated(params);
    }

    this.updateCount(params);
  };

  onFirstDataRendered = params => {
    const { onFirstDataRendered } = this.props;

    if (onFirstDataRendered) {
      onFirstDataRendered(params);
    }

    this.updateCount(params);
  };

  render() {
    const { first, last, count } = this.state;

    return (
      <div style={{ height: '100%' }}>
        <div style={{ height: 'calc(100% - 40px)' }}>
          <AgGridReact
            {...this.props}
            onBodyScroll={this.onBodyScroll}
            onFilterChanged={this.onFilterChanged}
            onGridReady={this.onGridReady}
            onGridSizeChanged={this.onGridSizeChanged}
            onRowDataUpdated={this.onRowDataUpdated}
            onFirstDataRendered={this.onFirstDataRendered}
          />
        </div>
        <div className="pt-4 text-right grid-count">
          <p>
            <span className="font-weight-bold">{first}</span> - <span className="font-weight-bold">{last}</span> of <span className="font-weight-bold"><NumberRenderer value={count}/></span>
          </p>
        </div>
      </div>
    );
  }
}
